import { Component, EventEmitter, Input, Output, SimpleChange, SimpleChanges } from '@angular/core';
import { FormGroup, FormBuilder, Validators, FormControl, AbstractControl } from '@angular/forms';
import { CreateUserRequest } from 'src/app/models/users/create-user-request.model';
import { UpdateUserRequest } from 'src/app/models/users/update-user-request.model';
import { User } from 'src/app/models/users/user.model';
import { DropdownValues } from '../store/users.state';
import { AppService } from 'src/app/app.service';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'rap-add-edit-user-ui',
  templateUrl: './add-edit-user.component.html',
  styleUrls: ['./add-edit-user.component.scss']
})
export class AddEditUserComponent {
  @Input() title: string;
  @Input() editableUser: User;
  @Input() roles: DropdownValues[];
  @Input() rosters: DropdownValues[];
  @Input() errorMessage: string;
  @Output() createNewUserEmitter = new EventEmitter<CreateUserRequest>();
  @Output() updateUserEmitter = new EventEmitter<UpdateUserRequest>();

  userForm: FormGroup;
  submitted: boolean = false;
  isLoading = false;
  public settings = {};
  public rolesSettings = {};

  constructor(private formBuilder: FormBuilder, private loaderService: AppService, private router: Router, private toastr: ToastrService) {
    this.loaderService.loading$.subscribe(isLoading => {
      console.log("isLoading")
      console.log(isLoading)
      this.isLoading = isLoading;
    });


    if (this.editableUser) {
      this.isLoading = true
    }
  }

  ngOnInit(): void {

    this.userForm = this.formBuilder.group({
      firstName: new FormControl(null, [Validators.required]),
      lastName: new FormControl(null, [Validators.required]),
      userName: new FormControl(null, [Validators.required]),
      email: new FormControl(null),
      role: new FormControl(null, [Validators.required]),
      studentRoster: new FormControl(null),
      active: [false, [Validators.required]],
      // No Validators.required here: allowMapperDelight is a boolean, where
      // `false` is a legitimate value that only passes `required` by accident of
      // Angular's isEmptyInputValue. 452 legacy rows carry allowMD = NULL, which
      // DID fail it - silently invalidating the entire form and making Save a
      // no-op for 319 live users. See also the !! coercion in ngOnChanges.
      allowMapperDelight: [false],
    });

    this.userForm.get('role')?.valueChanges.subscribe((role) => {
      const studentRosterControl = this.userForm.get('studentRoster');
      if (role && this.isStudentRosterRequired(role)) {
        studentRosterControl?.setValidators([Validators.required]);
      } else {
        studentRosterControl?.clearValidators();
      }
      studentRosterControl?.updateValueAndValidity();
    });

    this.isLoading = true;
    this.settings = {
      singleSelection: true,
      idField: 'id',
      textField: 'label',
      allowSearchFilter: true, // Allows search filter
      closeDropDownOnSelection: true, // Close dropdown after selection
      enableCheckAll: false,  // Disable "Select All" option
      itemsShowLimit: 1,      // Only show selected item in dropdown
      unSelectAllText: null,  // No "Unselect All" text
      selectAllText: null,    // No "Select All" text
      allowCheckboxes: false  // 
    };

    this.rolesSettings = {
      singleSelection: true,
      idField: 'id',
      textField: 'label',
      allowSearchFilter: true, // Allows search filter
      closeDropDownOnSelection: true, // Close dropdown after selection
      enableCheckAll: false,  // Disable "Select All" option
      itemsShowLimit: 1,      // Only show selected item in dropdown
      unSelectAllText: null,  // No "Unselect All" text
      selectAllText: null,    // No "Select All" text
      allowCheckboxes: false  // 
    };
  }

  ngOnChanges(changes: SimpleChanges) {

    if (changes['editableUser']?.currentValue) {
      this.isLoading = false;
      console.log(this.editableUser.studentRosterId)
      var v = { "id": this.editableUser.role.id, "label": this.editableUser.role.name }
      console.log(v)

      this.userForm.patchValue({
        firstName: this.editableUser.firstName,
        lastName: this.editableUser.lastName,
        userName: this.editableUser.userName,
        email: this.editableUser.email,
        role: v,
        studentRoster: this.editableUser.student_roster != null ? { "id": this.editableUser.student_roster.id, "label": this.editableUser.student_roster.name } : null,
        active: this.editableUser.active,
        // Coerce: allowMD is NULL on 452 legacy rows. Patching NULL straight in
        // left the control null rather than false.
        allowMapperDelight: !!this.editableUser.allowMD,
      });
      console.log(this.userForm.value.role)
    } else {
      this.isLoading = false;
    }
  }

  get f(): { [key: string]: AbstractControl } {
    return this.userForm.controls;
  }

  compareDropdownValues(a: DropdownValues, b: DropdownValues): boolean {
    return a && b ? a.id === b.id : a === b;
  }

  isStudentRosterRequired(role: DropdownValues): boolean {
    return role?.label?.toLowerCase() === 'research educator';
  }

  addUpdateUser() {
    this.submitted = true;

    // The class fix. Previously this method was `if (this.userForm.valid) {...}`
    // with no else, so an invalid form returned silently: no PUT, no toast, no
    // navigation, nothing to indicate why Save did nothing. Any control whose
    // validator existing data could violate became an invisible dead end -
    // allowMD = NULL alone accounted for 319 live users. Controls with no
    // per-field error markup (the two checkboxes) had no other way to surface.
    // This branch guarantees feedback for every field, present and future.
    if (this.userForm.invalid) {
      this.userForm.markAllAsTouched();
      this.toastr.error('Please complete the required fields', '', {
        positionClass: 'toast-top-center',
        closeButton: false,
      });
      return;
    }

    this.isLoading = true;
    var roleId = this.userForm.value.role.id;

    if (this.editableUser) {
      let userRequest: UpdateUserRequest = {
        id: this.editableUser.id,
        data: {
          firstName: this.userForm.value.firstName,
          lastName: this.userForm.value.lastName,
          userName: this.userForm.value.userName,
          emails: [this.userForm.value.email],
          roleId: roleId,
          studentRosterId: this.userForm.value.studentRoster?.id ?? "",
          active: this.userForm.value.active,
          allowMD: this.userForm.value.allowMapperDelight,
        },
      };

      this.updateUserEmitter.emit(userRequest);
    } else {
      let userRequest: CreateUserRequest = {
        data: {
          firstName: this.userForm.value.firstName,
          lastName: this.userForm.value.lastName,
          userName: this.userForm.value.userName,
          emails: [this.userForm.value.email],
          roleId: roleId,
          studentRosterId: this.userForm.value.studentRoster?.id ?? "",
          active: this.userForm.value.active,
          role: roleId,
          student_roster: this.userForm.value.studentRoster?.label ?? "",
        },
      };

      this.createNewUserEmitter.emit(userRequest);
    }
  }

  onReset() {
    this.userForm.reset();
  }

  Cancel() {
    this.router.navigate(['/dashboard/users'])
  }

  ngOnDestroy() {
    this.isLoading = false;
    this.userForm.reset();
  }
}
